import Text from 'components/_UI/Text';
import NotResultDashBoard from 'components/commons/NotResultDashBoard';
import date from 'helpers/date';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import { BarChart2, TrendingUp, TrendingDown, Eye, Award } from 'react-feather';

const ClicksStatistics = ({ clicks }) => {

    const formattedDates = clicks?.map(el => `${date.getMounthName(el.month.toString())} - ${el.year}`);
    const shortDates = clicks?.map(el => {
        const name = date.getMounthName(el.month.toString());
        return name ? name.substring(0, 3) : '';
    });
    const views = clicks?.map(el => el.views);

    const stats = useMemo(() => {
        if (!clicks?.length) return null;

        const totalVisits = clicks.reduce((sum, el) => sum + (el.views || 0), 0);

        let peakIdx = 0;
        clicks.forEach((el, i) => {
            if ((el.views || 0) > (clicks[peakIdx]?.views || 0)) peakIdx = i;
        });
        const peakMonth = date.getMounthName(clicks[peakIdx].month.toString());
        const peakValue = clicks[peakIdx].views || 0;

        const current = clicks[clicks.length - 1]?.views || 0;
        const previous = clicks.length > 1 ? (clicks[clicks.length - 2]?.views || 0) : 0;
        const trendPercent = previous > 0
            ? Math.round(((current - previous) / previous) * 100)
            : current > 0 ? 100 : 0;
        const trendUp = trendPercent >= 0;

        return { totalVisits, peakMonth, peakValue, current, trendPercent, trendUp };
    }, [clicks]);

    const data = {
        labels: shortDates,
        datasets: [
            {
                label: 'Visitas',
                data: views,
                backgroundColor: 'rgba(63, 107, 198, 0.65)',
                hoverBackgroundColor: colors.primaryLight,
                borderRadius: 6,
                borderSkipped: false,
                barPercentage: 0.6,
                categoryPercentage: 0.7,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: colors.nightDark,
                titleFont: { size: 11, weight: '400' },
                bodyFont: { size: 13, weight: '700' },
                padding: { top: 8, bottom: 8, left: 12, right: 12 },
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: (items) => formattedDates?.[items[0]?.dataIndex] || '',
                    label: (item) => `${item.raw} visitas`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: colors.night,
                    font: { size: 11 },
                    padding: 4,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.04)',
                    drawBorder: false,
                },
                border: { display: false, dash: [4, 4] },
                ticks: {
                    color: colors.nightLight,
                    font: { size: 11 },
                    padding: 8,
                    maxTicksLimit: 5,
                },
            },
        },
    };

    return (
        <Container>
            {clicks?.length ? (
                <>
                    <Header>
                        <HeaderLeft>
                            <IconBox>
                                <BarChart2 size={18} color={colors.neutralLight} />
                            </IconBox>
                            <HeaderText>
                                <Title>Visitas ao perfil</Title>
                                <Subtitle>Acompanhe o desempenho mensal</Subtitle>
                            </HeaderText>
                        </HeaderLeft>
                    </Header>

                    {stats && (
                        <StatsRow>
                            <StatCard index={0}>
                                <StatIconWrap accent="primary">
                                    <Eye size={14} color={colors.primaryLight} />
                                </StatIconWrap>
                                <StatInfo>
                                    <StatValue>{stats.totalVisits.toLocaleString('pt-BR')}</StatValue>
                                    <StatLabel>Total de visitas</StatLabel>
                                </StatInfo>
                            </StatCard>

                            <StatCard index={1}>
                                <StatIconWrap accent="gold">
                                    <Award size={14} color={colors.gold} />
                                </StatIconWrap>
                                <StatInfo>
                                    <StatValue>{stats.peakValue}</StatValue>
                                    <StatLabel>{stats.peakMonth}</StatLabel>
                                </StatInfo>
                            </StatCard>

                            <StatCard index={2}>
                                <StatIconWrap accent={stats.trendUp ? 'success' : 'danger'}>
                                    {stats.trendUp
                                        ? <TrendingUp size={14} color={colors.success} />
                                        : <TrendingDown size={14} color={colors.danger} />
                                    }
                                </StatIconWrap>
                                <StatInfo>
                                    <StatValue trend={stats.trendUp}>
                                        {stats.trendUp ? '+' : ''}{stats.trendPercent}%
                                    </StatValue>
                                    <StatLabel>vs. mês anterior</StatLabel>
                                </StatInfo>
                            </StatCard>
                        </StatsRow>
                    )}

                    <ChartWrapper>
                        <Bar data={data} options={options} />
                    </ChartWrapper>
                </>
            ) : (
                <NotResultDashBoard
                    ilustration={'views'}
                    text={
                        <>
                            <Text name='highlight'>
                                Ainda não há visitas no perfil do seu estabelecimento.
                            </Text>
                            <Text textAlign={'center'} marginTop={'5px'}>
                                Assim que você começar a receber visitas, o gráfico de visitas será exibido aqui,
                                permitindo que você acompanhe o desempenho do seu perfil em tempo real.
                            </Text>
                        </>
                    }
                />
            )}
        </Container>
    );
};

const fadeSlideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Container = styled.div`
    background: ${colors.neutralLight};
    width: 100%;
    border-radius: 12px;
    padding: 24px;
    margin-top: 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.05);

    @media screen and (max-width: ${breakpoints.sm}) {
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const IconBox = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 3px 10px rgba(63, 107, 198, 0.25);
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const Title = styled.h3`
    font-size: ${fonts.sizes.highlight};
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.2;
`;

const Subtitle = styled.p`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin: 0;
`;

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 24px;

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
        gap: 8px;
        margin-bottom: 20px;
    }
`;

const StatCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: ${colors.neutral};
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.03);
    animation: ${fadeSlideUp} 0.4s ease forwards;
    animation-delay: ${({ index }) => index * 0.08}s;
    opacity: 0;

    @media screen and (max-width: ${breakpoints.sm}) {
        padding: 10px 12px;
    }
`;

const StatIconWrap = styled.div`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    ${({ accent }) => accent === 'primary' && `
        background: rgba(79, 134, 247, 0.1);
    `}
    ${({ accent }) => accent === 'gold' && `
        background: rgba(222, 171, 87, 0.1);
    `}
    ${({ accent }) => accent === 'success' && `
        background: rgba(43, 200, 102, 0.1);
    `}
    ${({ accent }) => accent === 'danger' && `
        background: rgba(231, 80, 90, 0.1);
    `}
`;

const StatInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const StatValue = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
    line-height: 1.2;

    ${({ trend }) => trend === true && `
        color: ${colors.successDark};
    `}
    ${({ trend }) => trend === false && `
        color: ${colors.dangerDark};
    `}
`;

const StatLabel = styled.span`
    font-size: ${fonts.sizes.mini};
    color: ${colors.night};
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ChartWrapper = styled.div`
    width: 100%;
    height: 220px;
    animation: ${fadeSlideUp} 0.5s ease forwards;
    animation-delay: 0.25s;
    opacity: 0;

    @media screen and (max-width: ${breakpoints.sm}) {
        height: 180px;
    }
`;

export default ClicksStatistics;
